import { api } from './apiClient';

// Types
export interface Project {
  id: number;
  project_code: string;
  project_name: string;
  description: string;
  customer_id: number | null;
  customer_name: string | null;
  project_manager_id: number | null;
  project_manager_name: string | null;
  start_date: string | null;
  end_date: string | null;
  estimated_budget: number;
  actual_cost: number;
  status: 'PLANNING' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
  progress_percent: number;
  created_at: string;
  updated_at: string;
  task_count?: number;
  completed_tasks?: number;
  resource_count?: number;
  phases?: ProjectPhase[];
  taskSummary?: TaskSummary;
  timeLogged?: { total_hours: number; billable_hours: number };
  estimatedBudgetFormatted?: string;
  actualCostFormatted?: string;
}

export interface ProjectPhase {
  id: number;
  project_id: number;
  phase_name: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  budget_allocation: number;
  actual_cost: number;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  progress_percent: number;
  task_count?: number;
  hours_spent?: number;
}

export interface ProjectTask {
  id: number;
  project_id: number;
  phase_id: number | null;
  task_code: string;
  title: string;
  description: string | null;
  assigned_to: number | null;
  assigned_to_name: string | null;
  status: 'TODO' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  start_date: string | null;
  due_date: string | null;
  estimated_hours: number | null;
  actual_hours: number;
  progress_percent: number;
  project_name?: string;
  project_code?: string;
  phase_name?: string;
  logged_hours?: number;
  created_at: string;
}

export interface ProjectResource {
  id: number;
  project_id: number;
  user_id: number | null;
  resource_name: string;
  resource_type: 'EMPLOYEE' | 'CONTRACTOR' | 'EQUIPMENT' | 'OTHER';
  role: string | null;
  hourly_rate: number | null;
  daily_rate: number | null;
  allocation_percent: number;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  notes: string | null;
  user_name?: string;
  user_email?: string;
  total_hours_logged?: number;
}

export interface TimeEntry {
  id: number;
  project_id: number;
  task_id: number | null;
  user_id: number;
  entry_date: string;
  hours: number;
  description: string | null;
  billable: boolean;
  billing_rate: number | null;
  approved: boolean;
  approved_by: number | null;
  approved_at: string | null;
  user_name?: string;
  task_title?: string;
  task_code?: string;
  project_name?: string;
  project_code?: string;
}

export interface ProjectExpense {
  id: number;
  project_id: number;
  phase_id: number | null;
  expense_date: string;
  expense_type: string;
  amount: number;
  description: string | null;
  reference_type: string | null;
  reference_id: number | null;
  cost_center_id: number | null;
  phase_name?: string;
  cost_center_name?: string;
}

export interface TaskSummary {
  total: number;
  todo: number;
  in_progress: number;
  completed: number;
  on_hold: number;
  estimated_hours: number;
  actual_hours: number;
}

export interface User {
  id: number;
  full_name: string;
  email: string;
  role: string;
}

export interface ProjectMetrics {
  projectId: number;
  projectName: string;
  status: string;
  progressPercent: number;
  budget: {
    estimated: number;
    actual: number;
    utilization: number;
    variance: number;
  };
  tasks: {
    total: number;
    completed: number;
    inProgress: number;
    overdue: number;
    completionRate: number;
  };
  time: {
    estimatedHours: number;
    actualHours: number;
    totalLogged: number;
    billableHours: number;
    approvedHours: number;
  };
  resources: {
    count: number;
  };
  dates: {
    startDate: string | null;
    endDate: string | null;
    daysRemaining: number | null;
  };
}

export interface ProjectsSummary {
  projects: {
    total_projects: number;
    planning: number;
    in_progress: number;
    on_hold: number;
    completed: number;
    cancelled: number;
    total_budget: number;
    total_cost: number;
  };
  tasks: {
    total_tasks: number;
    completed_tasks: number;
    overdue_tasks: number;
  };
  time: {
    total_hours: number;
    billable_hours: number;
  };
}

// API response types (already unwrapped by apiClient)
interface ProjectListResponse {
  projects: Project[];
  count: number;
}

interface ProjectResponse {
  project: Project;
}

interface TaskListResponse {
  tasks: ProjectTask[];
  count: number;
}

interface ResourceListResponse {
  resources: ProjectResource[];
  count: number;
}

interface TimeEntryListResponse {
  entries: TimeEntry[];
  count: number;
  totalHours?: string;
  billableHours?: string;
}

interface ExpenseListResponse {
  expenses: ProjectExpense[];
  count: number;
  totalExpenses: string;
  totalExpensesFormatted: string;
}

interface PhaseListResponse {
  phases: ProjectPhase[];
}

interface MetricsResponse {
  metrics: ProjectMetrics;
}

interface SummaryResponse {
  summary: ProjectsSummary;
}

export const projectService = {
  // =============================================
  // PROJECTS
  // =============================================

  getProjects: async (filters?: {
    status?: string;
    customerId?: number;
    managerId?: number;
    search?: string;
  }): Promise<Project[]> => {
    const response = await api.get<ProjectListResponse>('/projects', { params: filters });
    return response.projects;
  },

  getProject: async (id: number): Promise<Project> => {
    const response = await api.get<ProjectResponse>(`/projects/${id}`);
    return response.project;
  },

  createProject: async (data: {
    projectName: string;
    description?: string;
    customerId?: number;
    projectManagerId?: number;
    startDate?: string;
    endDate?: string;
    estimatedBudget?: number;
    status?: string;
  }): Promise<Project> => {
    const response = await api.post<any>('/projects', data);
    return response.project;
  },

  updateProject: async (id: number, data: Partial<{
    projectName: string;
    description: string;
    customerId: number;
    projectManagerId: number;
    startDate: string;
    endDate: string;
    estimatedBudget: number;
    actualCost: number;
    status: string;
    progressPercent: number;
  }>): Promise<Project> => {
    const response = await api.put<any>(`/projects/${id}`, data);
    return response.project;
  },

  deleteProject: async (id: number): Promise<void> => {
    await api.delete(`/projects/${id}`);
  },

  // =============================================
  // PHASES
  // =============================================

  getProjectPhases: async (projectId: number): Promise<ProjectPhase[]> => {
    const response = await api.get<PhaseListResponse>(`/projects/${projectId}/phases`);
    return response.phases;
  },

  createPhase: async (projectId: number, data: {
    phaseName: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    budgetAllocation?: number;
    status?: string;
  }): Promise<ProjectPhase> => {
    const response = await api.post<any>(`/projects/${projectId}/phases`, data);
    return response.phase;
  },

  updatePhase: async (phaseId: number, data: Partial<{
    phaseName: string;
    description: string;
    startDate: string;
    endDate: string;
    budgetAllocation: number;
    actualCost: number;
    status: string;
    progressPercent: number;
  }>): Promise<ProjectPhase> => {
    const response = await api.put<any>(`/projects/phases/${phaseId}`, data);
    return response.phase;
  },

  deletePhase: async (phaseId: number): Promise<void> => {
    await api.delete(`/projects/phases/${phaseId}`);
  },

  // =============================================
  // TASKS
  // =============================================

  getAllTasks: async (filters?: {
    status?: string;
    assignedTo?: number;
    projectId?: number;
  }): Promise<ProjectTask[]> => {
    const response = await api.get<TaskListResponse>('/projects/tasks', { params: filters });
    return response.tasks;
  },

  getProjectTasks: async (projectId: number, filters?: {
    status?: string;
    assignedTo?: number;
    phaseId?: number;
    priority?: string;
  }): Promise<ProjectTask[]> => {
    const response = await api.get<TaskListResponse>(`/projects/${projectId}/tasks`, { params: filters });
    return response.tasks;
  },

  getTask: async (taskId: number): Promise<ProjectTask> => {
    const response = await api.get<any>(`/projects/tasks/${taskId}`);
    return response.task;
  },

  createTask: async (projectId: number, data: {
    phaseId?: number;
    title: string;
    description?: string;
    assignedTo?: number;
    status?: string;
    priority?: string;
    startDate?: string;
    dueDate?: string;
    estimatedHours?: number;
    parentTaskId?: number;
  }): Promise<ProjectTask> => {
    const response = await api.post<any>(`/projects/${projectId}/tasks`, data);
    return response.task;
  },

  updateTask: async (taskId: number, data: Partial<{
    phaseId: number;
    title: string;
    description: string;
    assignedTo: number;
    status: string;
    priority: string;
    startDate: string;
    dueDate: string;
    estimatedHours: number;
    actualHours: number;
    progressPercent: number;
  }>): Promise<ProjectTask> => {
    const response = await api.put<any>(`/projects/tasks/${taskId}`, data);
    return response.task;
  },

  deleteTask: async (taskId: number): Promise<void> => {
    await api.delete(`/projects/tasks/${taskId}`);
  },

  // =============================================
  // RESOURCES
  // =============================================

  getProjectResources: async (projectId: number): Promise<ProjectResource[]> => {
    const response = await api.get<ResourceListResponse>(`/projects/${projectId}/resources`);
    return response.resources;
  },

  assignResource: async (projectId: number, data: {
    userId?: number;
    resourceName?: string;
    resourceType?: string;
    role?: string;
    hourlyRate?: number;
    dailyRate?: number;
    allocationPercent?: number;
    startDate?: string;
    endDate?: string;
    notes?: string;
  }): Promise<ProjectResource> => {
    const response = await api.post<any>(`/projects/${projectId}/resources`, data);
    return response.resource;
  },

  updateResource: async (resourceId: number, data: Partial<{
    resourceName: string;
    resourceType: string;
    role: string;
    hourlyRate: number;
    dailyRate: number;
    allocationPercent: number;
    startDate: string;
    endDate: string;
    isActive: boolean;
    notes: string;
  }>): Promise<ProjectResource> => {
    const response = await api.put<any>(`/projects/resources/${resourceId}`, data);
    return response.resource;
  },

  removeResource: async (resourceId: number): Promise<void> => {
    await api.delete(`/projects/resources/${resourceId}`);
  },

  // =============================================
  // TIME TRACKING
  // =============================================

  getAllTimeEntries: async (filters?: {
    userId?: number;
    projectId?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<{ entries: TimeEntry[]; totalHours: string; billableHours: string }> => {
    const response = await api.get<TimeEntryListResponse>('/projects/time-entries', { params: filters });
    return {
      entries: response.entries,
      totalHours: response.totalHours || '0',
      billableHours: response.billableHours || '0'
    };
  },

  getProjectTimeEntries: async (projectId: number, filters?: {
    userId?: number;
    taskId?: number;
    startDate?: string;
    endDate?: string;
    approved?: boolean;
    billable?: boolean;
  }): Promise<TimeEntry[]> => {
    const response = await api.get<TimeEntryListResponse>(`/projects/${projectId}/time-entries`, { params: filters });
    return response.entries;
  },

  createTimeEntry: async (projectId: number, data: {
    taskId?: number;
    entryDate: string;
    hours: number;
    description?: string;
    billable?: boolean;
    billingRate?: number;
  }): Promise<TimeEntry> => {
    const response = await api.post<any>(`/projects/${projectId}/time-entries`, data);
    return response.entry;
  },

  updateTimeEntry: async (entryId: number, data: Partial<{
    hours: number;
    description: string;
    billable: boolean;
    billingRate: number;
    approved: boolean;
  }>): Promise<TimeEntry> => {
    const response = await api.put<any>(`/projects/time-entries/${entryId}`, data);
    return response.entry;
  },

  deleteTimeEntry: async (entryId: number): Promise<void> => {
    await api.delete(`/projects/time-entries/${entryId}`);
  },

  // =============================================
  // EXPENSES
  // =============================================

  getProjectExpenses: async (projectId: number): Promise<{
    expenses: ProjectExpense[];
    totalExpenses: string;
    totalExpensesFormatted: string;
  }> => {
    const response = await api.get<ExpenseListResponse>(`/projects/${projectId}/expenses`);
    return {
      expenses: response.expenses,
      totalExpenses: response.totalExpenses,
      totalExpensesFormatted: response.totalExpensesFormatted
    };
  },

  createExpense: async (projectId: number, data: {
    phaseId?: number;
    expenseDate: string;
    expenseType: string;
    amount: number;
    description?: string;
    referenceType?: string;
    referenceId?: number;
    costCenterId?: number;
  }): Promise<ProjectExpense> => {
    const response = await api.post<any>(`/projects/${projectId}/expenses`, data);
    return response.expense;
  },

  // =============================================
  // METRICS & REPORTS
  // =============================================

  getProjectMetrics: async (projectId: number): Promise<ProjectMetrics> => {
    const response = await api.get<MetricsResponse>(`/projects/${projectId}/metrics`);
    return response.metrics;
  },

  getProjectsSummary: async (): Promise<ProjectsSummary> => {
    const response = await api.get<SummaryResponse>('/projects/reports/summary');
    return response.summary;
  },

  getResourceUtilization: async (projectId?: number): Promise<any[]> => {
    const response = await api.get<{ utilization: any[] }>('/projects/reports/resource-utilization', {
      params: { projectId }
    });
    return response.utilization;
  },

  getUsers: async (): Promise<User[]> => {
    const response = await api.get<{ users: User[] }>('/projects/users');
    return response.users;
  },

  createUser: async (data: { fullName: string; email: string; role?: string }): Promise<User> => {
    const response = await api.post<{ user: User }>('/projects/users', data);
    return response.user;
  },
};
